import { rrulestr } from "rrule";
import { format } from "date-fns/format";

import type { Signal } from "@/Connect/types";
import { WEEK_DAYS, TIMEZONES } from "@/Connect/consts";

import parseTemperatureValueString from "@/Connect/utils/parseTemperatureValueString";

const DateDisplayFormat = "EEE LLL dd yyyy HH:mm";

const OperatorLabelMap = {
    "<": "Less than",
    "<=": "Less than and equal to",
    "===": "Equal to",
    ">=": "Greater than and equal to",
    ">": "Greater than"
} as const;

const SignalTypeLabelMap = {
    OPEN_WEATHER_RAIN: "Rain",
    OPEN_WEATHER_SNOW: "Snow",
    OPEN_WEATHER_TEMPERATURE: "Temperature",
    DATETIME: "Date Time"
};

const PrecipitationLabelMap = {
    LIGHT: "Light",
    MODERATE: "Moderate",
    HEAVY: "Heavy"
} as const;

function getSignalDescription(signal: Signal) {
    switch (signal.signalType) {
        case "DATETIME": {
            let description = "";

            const editedRule = rrulestr(signal.value);

            const startDate = format(editedRule.options.dtstart, DateDisplayFormat);
            const endDate = editedRule.options.until ? format(editedRule.options.until, DateDisplayFormat) : null;

            description = "Signal based on <strong>Date and Time</strong> which triggers";

            if (!endDate) {
                description += ` from <strong>${startDate}</strong>`;
            } else {
                description += ` between <strong>${startDate}</strong> and <strong>${endDate}</strong>`;
            }

            if (editedRule.options.byweekday) {
                const days = editedRule.options.byweekday.map(day => WEEK_DAYS[day].label);

                description += ` across these days <strong>${days.join(", ")}</strong>`;
            }

            if (editedRule.options.tzid) {
                const timezone = TIMEZONES.find(tz => tz.value === editedRule.options.tzid);

                description += ` in this time zone of <strong>${timezone?.label}</strong>`;
            }

            return `${description}.`;
        }

        case "OPEN_WEATHER_TEMPERATURE": {
            let description =
                "Signal based on <strong>Weather</strong> which triggers when <strong>Temperature</strong> is";

            const { operator, value } = parseTemperatureValueString(signal.value);

            if (operator) {
                description += ` <strong>${OperatorLabelMap[operator]}</strong> than <strong>${value}°C</strong>`;
            }

            if (signal?.locations?.length) {
                const locationName = signal.locations[0].locationRef.split(":q:").slice(-1)[0];
                description += ` in the location of <strong>${locationName}</strong>`;
            }

            return `${description}.`;
        }

        case "OPEN_WEATHER_RAIN":
        case "OPEN_WEATHER_SNOW":
        default: {
            const weatherSignalName = SignalTypeLabelMap[signal.signalType];
            const precipitationAmmountName = PrecipitationLabelMap[signal.value];

            let description = `Signal based on <strong>Weather</strong> which triggers when <strong>${weatherSignalName}</strong> is <strong>${precipitationAmmountName}</strong>`;

            if (signal?.locations?.length) {
                const locationName = signal.locations[0].locationRef.split(":q:").slice(-1)[0];
                description += ` in the location of <strong>${locationName}</strong>`;
            }

            return `${description}.`;
        }
    }
}

export default getSignalDescription;
